import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErrorMsg, formatINR } from "../lib/api";
import { emitDataChange, useDataSync, useAutoRefresh } from "../lib/dataSync";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CheckCircle, XCircle, Clock, Building2, MapPin, Layers, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ProjectForm from "../components/project/ProjectForm";
import { useProjectStatusLabels } from "../lib/projectSchemaApi";

export default function AdminProjectsTab() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  // v2.55 — Admin can edit/delete any project from the list
  const [editingProject, setEditingProject] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Phase I-B.1 — Schema-driven status labels.
  const { statusLabel } = useProjectStatusLabels();

  const load = () => api.get("/admin/projects").then((r) => setProjects(r.data));

  useEffect(() => { load(); }, []);

  // v2.72 — Live sync (builder submissions, approvals) + 45 s poll.
  useDataSync(["projects", "approvals"], load);
  useAutoRefresh(load, 45000);

  // v2.71.103 — Deep-link handler. AdminApprovalsTab's "Open in full editor"
  // routes project items here with `?edit=<id>`; auto-open the inline editor
  // and strip the param so refresh/back doesn't re-open it.
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || editingProject) return;
    const proj = projects.find((p) => p.id === editId);
    if (proj) {
      setEditingProject(proj);
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    } else if (projects.length > 0) {
      // Projects loaded but the targeted id isn't in admin's list → surface error.
      toast.error("Project not found in your queue.");
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, projects, editingProject]); // eslint-disable-line

  const approve = async (id) => {
    try { await api.post(`/admin/projects/${id}/approve`); toast.success("Approved"); emitDataChange(["projects", "approvals", "dashboard"]); }
    catch (e) { toast.error(apiErrorMsg(e, "Failed")); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/projects/${id}/reject`); toast.success("Rejected"); emitDataChange(["projects", "approvals", "dashboard"]); }
    catch (e) { toast.error(apiErrorMsg(e, "Failed")); }
  };
  const removeProject = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/builder/projects/${p.id}`);
      toast.success("Project deleted");
      // v2.72 — Optimistic removal + bus announce (subscription reconciles).
      setProjects((prev) => prev.filter((x) => x.id !== p.id));
      emitDataChange(["projects", "approvals", "dashboard"]);
    } catch (e) { toast.error(apiErrorMsg(e, "Delete failed")); }
  };

  const filtered = filter === "all" ? projects : projects.filter((p) => p.approval_status === filter);

  const counts = projects.reduce((acc, p) => { acc[p.approval_status] = (acc[p.approval_status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="admin-projects-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 border rounded-sm transition-colors ${
                filter === f ? "bg-[#09090B] text-white border-[#09090B]" : "bg-white border-[#E4E4E7] text-[#52525B] hover:border-[#09090B]"
              }`}
              data-testid={`projects-filter-${f}`}
            >
              {f}{f !== "all" && counts[f] ? ` (${counts[f]})` : ""}
            </button>
          ))}
        </div>
        <Button onClick={() => setShowAdd(true)} className="btn-brand rounded-sm" data-testid="admin-add-project-btn">
          <Plus size={13} className="mr-1.5" /> Add Project
        </Button>
      </div>

      {filtered.length === 0 && <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm text-[#A1A1AA]">No projects.</div>}

      <div className="grid lg:grid-cols-2 gap-3">
        {filtered.map((p) => (
          <div key={p.id} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`admin-project-${p.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] bg-[#F4F4F5] px-2 py-0.5 rounded-sm border border-[#E4E4E7]" data-testid={`admin-project-${p.id}-status-label`}>{statusLabel(p.status)}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 ${
                    p.approval_status === "approved" ? "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]" :
                    p.approval_status === "rejected" ? "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]" :
                    "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"
                  }`}>
                    {p.approval_status === "approved" ? <CheckCircle size={10} /> : p.approval_status === "rejected" ? <XCircle size={10} /> : <Clock size={10} />}
                    {p.approval_status}
                  </span>
                </div>
                <h4 className="font-heading font-semibold text-[16px] mt-1.5">{p.name}</h4>
                <div className="text-[12px] text-[#52525B] flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                  <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.location || "—"}</span>
                  <span className="inline-flex items-center gap-1"><Building2 size={11} /> {p.builder_name || "—"}</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 my-2 text-[11px]">
              {p.expected_possession && <Spec label="Possession" value={p.expected_possession} />}
              {false && <Spec icon={null} label="" value="" />}
              <Spec icon={<Layers size={10} />} label="Units" value={(p.units || []).length} />
              {(() => {
                const minPrice = Math.min(...(p.units || []).map(u => u.price_min || 0).filter(x => x > 0));
                return Number.isFinite(minPrice) && minPrice > 0
                  ? <Spec label="Starting" value={formatINR(minPrice)} />
                  : null;
              })()}
            </div>
            {p.description && <p className="text-[11px] text-[#52525B] line-clamp-2">{p.description}</p>}
            <div className="flex gap-2 pt-3 mt-2 border-t border-[#F4F4F5] flex-wrap">
              {p.approval_status !== "approved" && (
                <Button size="sm" className="rounded-sm h-7 text-[11px] bg-[#16A34A] hover:bg-[#15803D]" onClick={() => approve(p.id)} data-testid={`approve-project-${p.id}`}>
                  <CheckCircle size={11} className="mr-1" /> Approve
                </Button>
              )}
              {p.approval_status !== "rejected" && (
                <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] text-[#DC2626] border-[#FECACA] hover:bg-[#FEE2E2]" onClick={() => reject(p.id)} data-testid={`reject-project-${p.id}`}>
                  <XCircle size={11} className="mr-1" /> Reject
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={() => setEditingProject(p)} data-testid={`admin-edit-project-${p.id}`}>
                <Pencil size={11} className="mr-1" /> Edit
              </Button>
              <Button size="sm" variant="ghost" className="rounded-sm h-7 text-[11px] text-[#DC2626] hover:bg-[#FEE2E2]" onClick={() => removeProject(p)} data-testid={`admin-delete-project-${p.id}`}>
                <Trash2 size={11} className="mr-1" /> Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="admin-add-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl">Add Project (as Admin — auto-approved)</DialogTitle>
          </DialogHeader>
          <ProjectForm
            mode="create"
            currentUser={user}
            onSaved={() => { setShowAdd(false); load(); }}
            onCancel={() => setShowAdd(false)}
          />
        </DialogContent>
      </Dialog>

      {/* v2.55 — Admin Edit Project dialog */}
      <Dialog open={!!editingProject} onOpenChange={(v) => { if (!v) setEditingProject(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="admin-edit-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl">Edit Project — {editingProject?.name}</DialogTitle>
          </DialogHeader>
          {editingProject && (
            <ProjectForm
              mode="edit"
              project={editingProject}
              currentUser={user}
              onSaved={() => { setEditingProject(null); load(); }}
              onCancel={() => setEditingProject(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Spec = ({ label, value, icon }) => (
  <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-1.5">
    <div className="text-[9px] uppercase tracking-wider font-semibold text-[#A1A1AA] inline-flex items-center gap-1">{icon} {label}</div>
    <div className="font-mono-data font-semibold text-[#09090B]">{value}</div>
  </div>
);
